/**
 * What a license takes away from a project or a dataspace, and what to do about it: the page that
 * stands in for a closed Designer or a closed dataspace, the banner on a read-only one, the
 * badges of the lists and the words of the refusals.
 *
 * The keys under `note`, `short` and `errors` are named after the server's reasons and codes
 * (`access::restriction`) and are looked up dynamically (`utils/spaceRestriction.ts`), so those names
 * are a contract with the API rather than free-form. `other` says it for a reason this client has no
 * words for yet. A `Named` or `Counted` variant is used when the department or the numbers are known.
 *
 * Deliberately not under `spaces`, which is about where a space lives, nor `noLicense`, which is
 * the server's own license.
 */
export default {
	spaceRestriction: {
		title: {
			designer: "The Designer is not available for this project",
			project: "This project is read-only",
			dataspace: "Processes can't be opened or started",
		},
		note: {
			designer: {
				server: "This server's license does not include the Designer, so no project can be opened or edited here. Only a server administrator can change that, in the control center's license settings.",
				department:
					"The license of its department does not include the Designer. A department owner can ask LiveReader to add it, or the project can be moved into another department.",
				departmentNamed:
					"The license of {department} does not include the Designer. A department owner can ask LiveReader to add it, or the project can be moved into another department.",
				other: "The Designer is not available for this project.",
			},
			locked: {
				department_unlicensed:
					"The department's license is not valid. A department owner can renew its key.",
				department_unlicensedNamed:
					"The license of {department} is not valid. A department owner can renew its key.",
				personal_spaces_disabled:
					"This server's license has no personal spaces. Move it into a department.",
				personal_member_limit:
					"This personal space has more members than the license allows. Remove members, or move it into a department.",
				personal_member_limitCounted:
					"This personal space has more members than the license allows ({members} of {limit}). Remove members, or move it into a department.",
				other: "This space is locked until its license is in order.",
			},
		},
		running:
			"Processes that are already running keep running, and you can still stop, restart, archive or delete them.",
		short: {
			designer: {
				server: "The server's license has no Designer.",
				department: "The department's license has no Designer.",
				other: "The Designer is not available.",
			},
			locked: {
				department_unlicensed: "The department's license is not valid.",
				personal_spaces_disabled: "The server's license has no personal spaces.",
				personal_member_limit: "More members than a personal space may have.",
				other: "Locked until its license is in order.",
			},
		},
		badge: {
			designer: "Designer closed",
			project: "Read-only",
			dataspace: "Locked",
		},
		fix: {
			members: "Manage members",
			move: "Choose a department",
			department: "Open {department}",
			departments: "Open departments",
			license: "Open the license settings",
		},
		tryAgain: "Try again",
		picker: {
			personalOff:
				"This server's license has no personal spaces, so a department is the only place for it.",
			noPlace:
				"This server's license has no personal spaces, and you may not create in any department.",
			noPersonalMove:
				"This server's license has no personal spaces, so it can't be made personal. It can be moved into another department.",
		},
		memberLimit: {
			hint: "A personal space may have {limit} members at most, the owner included. Places taken, pending invitations counted: {taken}.",
		},
		errors: {
			personal_spaces_disabled:
				"This server's license has no personal spaces. Create it in a department instead.",
			designer_unavailable: {
				server: "This server's license does not include the Designer, so no project can be created.",
				department:
					"The license of that department does not include the Designer, so no project can be created in it.",
				other: "The Designer is not available there.",
			},
			space_locked: {
				department_unlicensed:
					"That department's license is not valid, so nothing can be created in it until a department owner renews its key.",
				personal_spaces_disabled:
					"This server's license has no personal spaces, so this one is locked. Move it into a department.",
				personal_member_limit:
					"This personal space has more members than the license allows, so it is locked. Remove members, or move it into a department.",
				other: "This space is locked until its license is in order.",
			},
			memberLimit: {
				limit: "This personal space may have no members at all. | This personal space may have at most one member. | This personal space may have at most {limit} members.",
				has: "It has none. | It has one member. | It has {members} members.",
				pending: "One invitation is pending. | {pending} invitations are pending.",
				addTail:
					"Remove members or cancel invitations first, or move the space into a department.",
				inviteeTail:
					"Ask whoever invited you to make room, or to move the space into a department.",
				moveTail:
					"Remove members before making it personal, or leave it in its department.",
				named: "{name}: {message}",
			},
		},
	},
};
